/**
 * How much of itself the trailing toolbar group can afford to show.
 *
 * Measured against the width of that group's own grid track, not the window:
 * the designer is embedded, so the window says nothing useful, and the track is
 * exactly the room the group has. The track is a `minmax(0, 1fr)` share of the
 * leftover space, so it does not move when the contents change — measuring it
 * cannot start a loop where shrinking the contents frees space that grows them
 * back.
 *
 * The order things are given up in is the order they are least used in: the
 * sample-data label, then the brand name, then the Save and Export words, and
 * only at the very end do the brand and sample pickers move into the overflow
 * menu. Design/Preview, zoom and Export are never shed — they live in the
 * centre track and a fixed-width button, which is what "protect space" means
 * in practice.
 *
 * The numbers come from measuring the real controls: ~646px for the trailing
 * group with every label, ~430 once the sample label goes, ~300 icon-only.
 */
export type Density = "full" | "labels" | "icons" | "overflow";

export function densityFor(trackWidth: number): Density {
  // Nothing measured yet. Assume room, because a toolbar that renders collapsed
  // and expands one frame later reads as a glitch. A *measured* zero is the
  // opposite situation and falls through to `overflow` below: the track really
  // has no room, which is what happens on a phone once the centre group has
  // taken the width, and assuming room there put the pickers off-screen.
  if (trackWidth < 0) return "full";
  if (trackWidth >= 620) return "full";
  if (trackWidth >= 420) return "labels";
  if (trackWidth >= 300) return "icons";
  return "overflow";
}

/** Whether a given piece of chrome survives at this density. */
export const shows = {
  sampleLabel: (d: Density) => d === "full",
  themeName: (d: Density) => d === "full" || d === "labels",
  saveText: (d: Density) => d === "full",
  exportText: (d: Density) => d !== "icons" && d !== "overflow",
  pickersInline: (d: Density) => d !== "overflow"
};
